import { Eye, HelpCircle, Share2, ThumbsUp } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/shared/lib/utils/cn';
import { useAuth, useAuthModal } from '@/features/auth';
import type { ReactionType } from '@/shared/types/tables';
import { useReactions, useToggleReaction } from '../api/useReactions';
import { emptyReactionCounts } from '../model/types';

const REACTIONS: { type: ReactionType; icon: LucideIcon; label: string }[] = [
  { type: 'useful', icon: ThumbsUp, label: 'Useful' },
  { type: 'eye', icon: Eye, label: 'Eye-opening' },
  { type: 'share', icon: Share2, label: 'Share-worthy' },
  { type: 'confused', icon: HelpCircle, label: 'Confusing' },
];

/** Article reaction row. Signed-out clicks open the auth modal. */
export function ReactionBar({ articleId }: { articleId: string }) {
  const { user } = useAuth();
  const openAuth = useAuthModal((state) => state.open);
  const { data } = useReactions(articleId);
  const toggle = useToggleReaction(articleId);

  const counts = data?.counts ?? emptyReactionCounts();
  const mine = data?.mine ?? null;

  const handleClick = (type: ReactionType) => {
    if (!user) {
      openAuth('login');
      return;
    }
    toggle.mutate({ type, remove: mine === type });
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      {REACTIONS.map(({ type, icon: Icon, label }) => {
        const active = mine === type;
        return (
          <button
            key={type}
            type="button"
            aria-pressed={active}
            onClick={() => handleClick(type)}
            className={cn(
              'flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition-all',
              active
                ? 'border-amber-400 bg-amber-50 text-amber-700 dark:bg-amber-950/30 dark:text-amber-300'
                : 'border-border text-muted-foreground hover:border-zinc-300 hover:text-foreground dark:hover:border-zinc-600',
            )}
          >
            <Icon className="h-4 w-4" />
            {label}
            {counts[type] > 0 ? (
              <span className="tabular-nums text-xs text-muted-foreground">{counts[type]}</span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
